﻿import { z } from 'zod';
import { PageTypeEnum } from '../types/pages.js';

export {
  PageTypeEnum,
  PageVersionSchema,
  PageSchema,
  PageDetailSchema,
  GetPagesResponseSchema,
  GetPageVersionsResponseSchema,
  TableauProviderDataSchema,
} from '../types/pages.js';

export type {
  PageType,
  PageVersion,
  Page,
  PageDetail,
  GetPagesResponse,
  GetPageVersionsResponse,
  TableauProviderData,
} from '../types/pages.js';

export const CreatePageSchema = z.object({
  name: z.string().min(1),
  type: PageTypeEnum,
  dataConnector: z.string().nullable().optional(),
  templateId: z.number().int().positive().nullable().optional(),
  isTemplate: z.boolean().optional(),
  /** For third_party pages: iframe URL, provider name, etc. */
  providerData: z.record(z.string(), z.unknown()).optional(),
});
export namespace CreatePage {
  export type Request = z.infer<typeof CreatePageSchema>;
}

export const UpdatePageSchema = z.object({
  name: z.string().min(1).optional(),
  dataConnector: z.string().nullable().optional(),
  isTemplate: z.boolean().optional(),
  providerData: z.record(z.string(), z.unknown()).optional(),
});
export namespace UpdatePage {
  export type Request = z.infer<typeof UpdatePageSchema>;
}

export const CreatePageVersionSchema = z.object({
  /** Full DashboardConfig JSON — validated against DashboardConfigSchema server-side */
  config: z.record(z.string(), z.unknown()),
  description: z.string().optional(),
});
export namespace CreatePageVersion {
  export type Request = z.infer<typeof CreatePageVersionSchema>;
}

export const ProxyWidgetDataSchema = z.object({
  /** FK to the agent record to proxy requests through */
  agentId: z.number().int(),
  /** Path appended to the agent's base URL (e.g. "/api/report") */
  extensionPath: z.string(),
  /** Optional brand identifier — when provided, the proxy resolves brand details and includes them in the agent request context */
  brandIdentifier: z.string().optional(),
  /** Optional subject context — e.g. HCP ID for profile widgets */
  subject: z
    .object({
      type: z.string(),
      id: z.number(),
    })
    .optional(),
});
export namespace ProxyWidgetData {
  export type Request = z.infer<typeof ProxyWidgetDataSchema>;
}

export const GetPagesQuerySchema = z.object({
  q: z.string().optional(),
  type: PageTypeEnum.optional(),
  isTemplate: z
    .enum(['true', 'false'])
    .transform((v) => v === 'true')
    .optional(),
});
/** Upper bound on page ids per batched lookup (`GET /pages?ids=`), enforced by the API and honored by the client. */
export const MAX_PAGE_IDS_PER_REQUEST = 50;

/**
 * Batched lookup selector: comma-separated canonical positive integers within Postgres `Int` range
 * (e.g. `3,17,42`). Parses to a de-duplicated number array; anything malformed or oversized is rejected, never trimmed.
 */
export const PageIdsQuerySchema = z
  .string()
  .regex(/^[1-9]\d{0,9}(,[1-9]\d{0,9})*$/)
  .transform((raw) => raw.split(',').map(Number))
  .pipe(z.array(z.number().int().max(2_147_483_647)).max(MAX_PAGE_IDS_PER_REQUEST))
  .transform((ids) => [...new Set(ids)]);

export namespace GetPages {
  export type Query = z.infer<typeof GetPagesQuerySchema>;
}
